import { useMemo } from 'react'

import {
  IntegrationsAgreggatedSyncableStateValuesEnum,
  SyncableStateUpdatedDocument,
} from 'generated/resources/graphql'

import { useSubscription } from 'modules/core/hooks/useSubscription'
import i18n from 'modules/core/lib/i18n'

import { usePrivateIntegrationsSyncContext } from 'modules/integrations/hooks/SyncContext'
import { useLeadingTrailingDebounce } from 'modules/integrations/hooks/useLeadingTrailingDebounce'
import { useSyncableStateStatistics } from 'modules/integrations/hooks/useSyncableStateStatistics'

const SYNCING_POLLING_INTERVAL_MS = 2 * 1000
const IDLE_POLLING_INTERVAL_MS = 3 * 60 * 1000

export type SyncBarStatus = 'pending' | 'syncing' | 'failed' | 'synced'

export type StatusCount = {
  status: IntegrationsAgreggatedSyncableStateValuesEnum
  count: number
  label: string
}

const translateStatusLabel = (
  status: IntegrationsAgreggatedSyncableStateValuesEnum,
  isExportMode: boolean
): string => {
  if (isExportMode) {
    switch (status) {
      case 'failed':
        return i18n.t('integrations.filters.export_status.failed')
      case 'partially_synced':
        return i18n.t('integrations.filters.export_status.partially_exported')
      case 'outdated':
        return i18n.t('integrations.filters.export_status.outdated')
      case 'pending':
        return i18n.t('integrations.filters.export_status.pending')
      case 'synced':
        return i18n.t('integrations.filters.export_status.exported')
      case 'syncing':
        return i18n.t('integrations.filters.export_status.exporting')
    }
  }
  switch (status) {
    case 'failed':
      return i18n.t('integrations.filters.sync_status.failed')
    case 'partially_synced':
      return i18n.t('integrations.filters.sync_status.partially_synced')
    case 'outdated':
      return i18n.t('integrations.filters.sync_status.outdated')
    case 'pending':
      return i18n.t('integrations.filters.sync_status.pending')
    case 'synced':
      return i18n.t('integrations.filters.sync_status.synced')
    case 'syncing':
      return i18n.t('integrations.filters.sync_status.syncing')
  }
}

const deriveSyncBarStatus = (
  statusCounts: Array<{ status: string; count: number }>,
  isSyncing: boolean
): SyncBarStatus => {
  const getCount = (status: string) => statusCounts.find((s) => s.status === status)?.count ?? 0

  if (getCount('syncing') > 0 || isSyncing) return 'syncing'
  if (getCount('failed') > 0 || getCount('partially_synced') > 0) return 'failed'
  if (getCount('pending') > 0 || getCount('outdated') > 0) return 'pending'
  return 'synced'
}

const translateSyncBarStatus = (status: SyncBarStatus, isExportMode: boolean): string => {
  if (isExportMode) {
    switch (status) {
      case 'pending':
        return i18n.t('integrations.sync_bar.export_status.pending')
      case 'syncing':
        return i18n.t('integrations.sync_bar.export_status.syncing')
      case 'failed':
        return i18n.t('integrations.sync_bar.export_status.failed')
      case 'synced':
        return i18n.t('integrations.sync_bar.export_status.synced')
    }
  }
  switch (status) {
    case 'pending':
      return i18n.t('integrations.sync_bar.status.pending')
    case 'syncing':
      return i18n.t('integrations.sync_bar.status.syncing')
    case 'failed':
      return i18n.t('integrations.sync_bar.status.failed')
    case 'synced':
      return i18n.t('integrations.sync_bar.status.synced')
  }
}

export const useSyncBarData = () => {
  const {
    dataCollectionSourceState,
    marketplaceIntegration,
    syncEnabled,
    configuration,
    isSyncing,
    capabilityMode,
  } = usePrivateIntegrationsSyncContext()

  const isExportMode = capabilityMode === 'file'

  const pollingEnabled = configuration.pollingEnabled ?? false

  const pollingIntervalMs = pollingEnabled
    ? isSyncing
      ? SYNCING_POLLING_INTERVAL_MS
      : IDLE_POLLING_INTERVAL_MS
    : null

  const { statistics, refetch } = useSyncableStateStatistics({
    skip: !syncEnabled,
    pollingIntervalMs,
  })

  const debouncedRefetch = useLeadingTrailingDebounce(refetch, 1000)

  // Use subscription-based updates when polling is disabled (standard mode)
  useSubscription(SyncableStateUpdatedDocument, {
    skip: !syncEnabled || pollingEnabled,
    onData: () => {
      debouncedRefetch()
    },
  })

  const rawStatusCounts = useMemo(() => statistics?.statusCounts ?? [], [statistics])

  const syncStatus: SyncBarStatus | null = useMemo(() => {
    if (!syncEnabled) return null
    if (rawStatusCounts.length === 0) return isSyncing ? 'syncing' : null
    return deriveSyncBarStatus(rawStatusCounts, isSyncing)
  }, [syncEnabled, isSyncing, rawStatusCounts])

  const syncStatusLabel = useMemo(
    () => (syncStatus ? translateSyncBarStatus(syncStatus, isExportMode) : null),
    [syncStatus, isExportMode]
  )

  const statusCounts = useMemo<StatusCount[]>(() => {
    if (!syncEnabled) return []

    const ALL_STATUSES: IntegrationsAgreggatedSyncableStateValuesEnum[] = [
      'failed',
      'partially_synced',
      'outdated',
      'pending',
      'synced',
      'syncing',
    ]

    return ALL_STATUSES.map((status) => ({
      status,
      count: rawStatusCounts.find((s) => s.status === status)?.count ?? 0,
      label: translateStatusLabel(status, isExportMode),
    })).filter((entry) => entry.count > 0)
  }, [syncEnabled, rawStatusCounts, isExportMode])

  return {
    syncStatus,
    syncStatusLabel,
    statusCounts,
    isExportMode,
  }
}
